import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { TextField, Link } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { validateText } from '@bingads-webui-campaign-react/smart-component-text';

import { getDurationOptions, messageMaxCount } from '../utils/booking-config';
import {
  getTimeInfo,
  getTimeOptions,
  getDisplayedDuration,
  getTimeInMinutes,
  getTimeString,
} from '../utils/get-available-timeslots';
import { BookingStepTime } from './booking-step-time';

const getStyles = () => ({
  container: {
    position: 'relative',
    width: '100%',
    padding: '20px 70px',

    '& .input-message': {
      marginTop: 13,
    },
  },
  contact: {
    marginBottom: 20,

    '& .bold': {
      fontWeight: 600,
    },

    '& .detail': {
      lineHeight: '20px',
    },
  },
  error: {
    color: 'red',
    position: 'absolute',
    bottom: 0,
  },
});

const textAreaStyle = {
  fieldGroup: {
    height: 134,
  },
};

const UnstyledAppointmentEdit = ({
  classes,
  i18n,
  logger,
  bookingDetail,
  existingBookings,
  appointmentHours,
  submitFnRef,
  updateAppointment,
  customerTimeZoneOffset,
  onFinished,
}) => {
  const {
    id,
    emailAddress,
    name,
    phoneNumber,
    time,
    timeInMinutes,
    message,
  } = bookingDetail;

  const durationOptions = useMemo(() => getDurationOptions(i18n), [i18n]);
  const { date, startTime } = useMemo(
    () => getTimeInfo(time, customerTimeZoneOffset),
    [time, customerTimeZoneOffset]
  );

  // Parse duration from timeInMinutes
  const [duration, setDuration] = useState(getDisplayedDuration(timeInMinutes));
  // Parse date from time
  const [selectedDate, setSelectedDate] = useState(date);
  const [selectedTime, setSelectedTime] = useState({ key: startTime, text: startTime });
  // Available start time
  const [availability, setAvailability] = useState([]);
  const [extraMessage, setExtraMessage] = useState({ value: message, error: '' });
  const [errorMessage, setErrorMessage] = useState('');

  const onSelectDate = useCallback((newDate) => {
    setSelectedDate(newDate);
    // Clear the selected time
    setSelectedTime({});
    setErrorMessage('');
  }, []);

  const onSelectDuration = useCallback((e, item) => {
    setDuration(item);
    // Clear the selected time
    setSelectedTime({});
    setErrorMessage('');
  }, []);

  const onSelectTime = useCallback((newTime) => {
    setSelectedTime(newTime);
    setErrorMessage('');
  }, []);

  const onChangeExtraMessage = useCallback((e, newValue) => {
    setExtraMessage({
      value: newValue,
      error: validateText(newValue, messageMaxCount, i18n).errorMessage,
    });
    setErrorMessage('');
  }, [i18n]);

  const onSubmit = () => {
    // No change, just close the modal
    if (
      duration.key === getDisplayedDuration(timeInMinutes).key
      && date.toString() === selectedDate.toString()
      && startTime === selectedTime.key
      && message === extraMessage.value
    ) {
      onFinished();
      return;
    }

    // Validation
    if (_.isEmpty(selectedTime)) {
      setErrorMessage(i18n.getString(_TL_('Please select specific time')));
      return;
    } else if (extraMessage.error) {
      return;
    }

    logger.logTrace('CTA|BookAppointment|EditAppointment');
    // Submit change
    updateAppointment({
      ...bookingDetail,
      time: getTimeString(selectedDate, selectedTime.key, customerTimeZoneOffset),
      timeInMinutes: getTimeInMinutes(duration.key),
      message: extraMessage.value,
    }).then(() => {
      onFinished();
    }).catch((error) => {
      setErrorMessage(error);

      logger.logError('CTAError|BookAppointment|EditAppointment', error);
      throw new Error(error);
    });
  };

  // eslint-disable-next-line no-param-reassign
  submitFnRef.current = onSubmit;

  // Get available start time
  useEffect(() => {
    // Filter out current booking, so that the current one can be shown.
    const filteredExistingBookings = existingBookings.filter(booking => booking.id !== id);

    const availableStartTime = getTimeOptions(
      appointmentHours,
      filteredExistingBookings,
      duration.key,
      selectedDate,
      customerTimeZoneOffset,
      customerTimeZoneOffset
    );

    setAvailability(availableStartTime);
  }, [appointmentHours, selectedDate, duration.key, existingBookings, id, customerTimeZoneOffset]);

  return (
    <div className={classes.container}>
      <div className={classNames(classes.contact, 'contact-info')}>
        <div className="bold">{i18n.getString(_TL_('Customer contact information'))}</div>
        <div className="detail">
          <div>{name}</div>
          <div>{phoneNumber}</div>
          <Link href={`mailto:${emailAddress}`} target="_blank">
            {emailAddress}
          </Link>
        </div>
      </div>
      <BookingStepTime
        className="editing-booking-time"
        i18n={i18n}
        availability={availability}
        durationOptions={durationOptions}
        selectedDuration={duration}
        onSelectDuration={onSelectDuration}
        selectedDate={selectedDate}
        onSelectDate={onSelectDate}
        selectedTime={selectedTime}
        onSelectTime={onSelectTime}
        timezoneOffset={customerTimeZoneOffset}
      />
      <TextField
        className="input-message"
        label={i18n.getString(_TL_('Comments to customer'))}
        placeholder={i18n.getString(_TL_('Tell your customer why you\'re changing their appointment.'))}
        value={extraMessage.value}
        errorMessage={extraMessage.error}
        onChange={onChangeExtraMessage}
        resizable={false}
        styles={textAreaStyle}
        multiline
      />
      {
        errorMessage && (
          <div className={classNames(classes.error, 'edit-appointment-error')}>
            <span>*{errorMessage}</span>
          </div>
        )
      }
    </div>
  );
};

UnstyledAppointmentEdit.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string,
    contact: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  bookingDetail: PropTypes.shape({
    id: PropTypes.string,
    emailAddress: PropTypes.string,
    name: PropTypes.string,
    phoneNumber: PropTypes.string,
    sequence: PropTypes.number,
    message: PropTypes.string,
    service: PropTypes.string,
    status: PropTypes.string,
    time: PropTypes.string,
    timeInMinutes: PropTypes.number,
  }).isRequired,
  existingBookings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  appointmentHours: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customerTimeZoneOffset: PropTypes.number.isRequired,
  updateAppointment: PropTypes.func.isRequired,
  submitFnRef: PropTypes.shape({}).isRequired,
  onFinished: PropTypes.func,
  logger: PropTypes.shape({
    logError: PropTypes.func,
    logTrace: PropTypes.func,
  }),
};

UnstyledAppointmentEdit.defaultProps = {
  onFinished: _.noop,
  logger: {
    logError: _.noop,
    logTrace: _.noop,
  },
};

export const AppointmentEdit = React.memo(withDefaultStyles(
  UnstyledAppointmentEdit,
  getStyles
));
